import { Component, createRef } from "react";
import teamAvatars from "./teamAvatars.js";

export default class DashboardMatchups extends Component {
  constructor(props) {
    super(props);
    this.listRef = createRef();
    this.carouselRef = createRef();
    this.cardRef = createRef();
    this.inactivityTimer = null;
    this.state = {
      isOverflowing: false,
      isActive: true,
    };
  }

  componentDidMount() {
    this.checkOverflow();
    window.addEventListener("resize", this.checkOverflow);
    if (this.cardRef.current) {
      this.cardRef.current.addEventListener("mousemove", this.handleMouseMove);
      this.cardRef.current.addEventListener("mouseenter", this.handleMouseMove);
    }
    this.startInactivityTimer();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.checkOverflow);
    if (this.cardRef.current) {
      this.cardRef.current.removeEventListener(
        "mousemove",
        this.handleMouseMove,
      );
      this.cardRef.current.removeEventListener(
        "mouseenter",
        this.handleMouseMove,
      );
    }
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.currentLeague !== this.props.currentLeague ||
      prevProps.currentWeek !== this.props.currentWeek
    ) {
      this.checkOverflow();
    }
  }

  checkOverflow = () => {
    if (this.listRef.current && this.carouselRef.current) {
      const listHeight = this.listRef.current.clientHeight;
      const carouselHeight = this.carouselRef.current.scrollHeight / 2; // Divided by 2 because content is duplicated
      const isOverflowing = carouselHeight > listHeight;
      if (this.state.isOverflowing !== isOverflowing) {
        this.setState({ isOverflowing });
      }
    }
  };

  handleMouseMove = () => {
    if (!this.state.isActive) {
      this.setState({ isActive: true });
    }
    this.startInactivityTimer();
  };

  startInactivityTimer = () => {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    this.inactivityTimer = setTimeout(() => {
      this.setState({ isActive: false });
    }, 10000);
  };

  renderTeamAvatar(team) {
    if (!team || team === "average") {
      return (
        <img
          src={teamAvatars["avg"]}
          alt="Average"
          className="matchup-avatar-img"
        />
      );
    }
    const avatarSrc =
      team.avatarId && teamAvatars[team.avatarId]
        ? teamAvatars[team.avatarId]
        : null;
    if (avatarSrc) {
      return (
        <img
          src={avatarSrc}
          alt={team.teamName}
          className="matchup-avatar-img"
        />
      );
    }
    return (
      <div className="matchup-avatar-placeholder">
        {team.teamName?.charAt(0) || "?"}
      </div>
    );
  }

  render() {
    const currentLeague = this.props.currentLeague;
    const currentWeek = this.props.currentWeek;
    const matches = currentLeague.matchPlan[currentWeek] || [];

    const LiveDot = () => {
      return (
        <svg height="25" width="25" className="blinking">
          <circle cx="12" cy="12" r="6" fill="crimson" />
        </svg>
      );
    };

    const renderMatchups = (keyPrefix = "") =>
      matches.map((match, idx) => {
        if (match === "average") {
          // Average matchup - all teams vs average
          const avgPoints = this.props.getWeekAveragePointsLeagueTeams(
            currentWeek,
            currentLeague.teams,
          );
          return currentLeague.teams.map((team) => {
            const teamPoints = this.props.getTeamAveragePoints(
              team,
              currentWeek,
            );
            const teamWins = teamPoints >= avgPoints;

            // Calculate green percentage (33% min, 67% max)
            const totalPoints = teamPoints + avgPoints;
            let greenPercent = 50;
            if (totalPoints > 0) {
              const winnerPoints = teamWins ? teamPoints : avgPoints;
              const ratio = winnerPoints / totalPoints;
              greenPercent = Math.min(67, Math.max(33, ratio * 100));
            }

            // If team wins, green is on left side (tilt right); if average wins, green is on right (tilt left)
            const gradientStyle = teamWins
              ? {
                  background: `linear-gradient(115deg, #5da970 0%, #5da970 ${greenPercent}%, #f9fafb ${greenPercent}%, #f9fafb 100%)`,
                }
              : {
                  background: `linear-gradient(65deg, #f9fafb 0%, #f9fafb ${
                    100 - greenPercent
                  }%, #5da970 ${100 - greenPercent}%, #5da970 100%)`,
                };

            return (
              <div
                className="matchup-item"
                key={`${keyPrefix}avg-${team.id}-${currentWeek}`}
                style={gradientStyle}
              >
                <div
                  className={`matchup-team matchup-team-left ${
                    teamWins ? "is-winner" : "is-loser"
                  }`}
                >
                  <div className="matchup-team-info">
                    <div className="matchup-top-row">
                      <div className="matchup-avatar">
                        {this.renderTeamAvatar(team)}
                      </div>
                      <span className="matchup-points">
                        {teamPoints.toFixed(0)}
                      </span>
                    </div>
                    <span className="matchup-team-name">{team.teamName}</span>
                  </div>
                </div>
                <div
                  className={`matchup-team matchup-team-right ${
                    !teamWins ? "is-winner" : "is-loser"
                  }`}
                >
                  <div className="matchup-team-info">
                    <div className="matchup-top-row">
                      <span className="matchup-points">{avgPoints}</span>
                      <div className="matchup-avatar">
                        {this.renderTeamAvatar("average")}
                      </div>
                    </div>
                    <span className="matchup-team-name">Gennemsnittet</span>
                  </div>
                </div>
              </div>
            );
          });
        } else {
          const teamOne = this.props.findTeam(match[0], currentLeague.id);
          const teamTwo = this.props.findTeam(match[1], currentLeague.id);

          let team1Points = 0;
          let team2Points = 0;

          if (teamOne === "average") {
            team1Points = this.props.getWeekAveragePointsLeagueTeams(
              currentWeek,
              currentLeague.teams,
            );
          } else {
            team1Points = this.props.getTeamAveragePoints(teamOne, currentWeek);
          }
          if (teamTwo === "average") {
            team2Points = this.props.getWeekAveragePointsLeagueTeams(
              currentWeek,
              currentLeague.teams,
            );
          } else {
            team2Points = this.props.getTeamAveragePoints(teamTwo, currentWeek);
          }

          const team1Wins = team1Points >= team2Points;

          // Calculate green percentage (33% min, 67% max)
          const totalPoints = team1Points + team2Points;
          let greenPercent = 50;
          if (totalPoints > 0) {
            const winnerPoints = team1Wins ? team1Points : team2Points;
            const ratio = winnerPoints / totalPoints;
            // Scale from 50% (equal) to 67% (dominant), minimum 33%
            greenPercent = Math.min(67, Math.max(33, ratio * 100));
          }

          // If team1 wins, green is on left side (tilt right); if team2 wins, green is on right (tilt left)
          const gradientStyle = team1Wins
            ? {
                background: `linear-gradient(115deg, #5da970 0%, #5da970 ${greenPercent}%, #f9fafb ${greenPercent}%, #f9fafb 100%)`,
              }
            : {
                background: `linear-gradient(65deg, #f9fafb 0%, #f9fafb ${
                  100 - greenPercent
                }%, #5da970 ${100 - greenPercent}%, #5da970 100%)`,
              };

          return (
            <div
              className="matchup-item"
              key={`${keyPrefix}${idx}-${match[0]}-${match[1]}-${currentWeek}`}
              style={gradientStyle}
            >
              <div
                className={`matchup-team matchup-team-left ${
                  team1Wins ? "is-winner" : "is-loser"
                }`}
              >
                <div className="matchup-team-info">
                  <div className="matchup-top-row">
                    <div className="matchup-avatar">
                      {this.renderTeamAvatar(teamOne)}
                    </div>
                    <span className="matchup-points">
                      {team1Points.toFixed(0)}
                    </span>
                  </div>
                  <span className="matchup-team-name">
                    {teamOne === "average"
                      ? "Gennemsnittet"
                      : teamOne?.teamName || "Unknown"}
                  </span>
                </div>
              </div>
              <div
                className={`matchup-team matchup-team-right ${
                  !team1Wins ? "is-winner" : "is-loser"
                }`}
              >
                <div className="matchup-team-info">
                  <div className="matchup-top-row">
                    <span className="matchup-points">
                      {team2Points.toFixed(0)}
                    </span>
                    <div className="matchup-avatar">
                      {this.renderTeamAvatar(teamTwo)}
                    </div>
                  </div>
                  <span className="matchup-team-name">
                    {teamTwo === "average"
                      ? "Gennemsnittet"
                      : teamTwo?.teamName || "Unknown"}
                  </span>
                </div>
              </div>
            </div>
          );
        }
      });

    return (
      <div className="matchup-card" ref={this.cardRef}>
        <div className="matchup-header">
          <button
            className={`matchup-nav-btn ${this.props.currentWeek <= 1 ? "disabled" : ""} ${
              !this.state.isActive ? "hidden" : ""
            }`}
            onClick={() => this.props.changeCurrentWeek(-1)}
            disabled={this.props.currentWeek <= 1}
          >
            <span>‹</span>
          </button>
          <div className="matchup-title">
            <LiveDot />
            <h1>Kampe</h1>
            <span
              className={`matchup-week-label ${!this.state.isActive ? "hidden" : ""}`}
            >
              UGE {currentWeek}
            </span>
          </div>
          <button
            className={`matchup-nav-btn ${
              this.props.currentWeek >= this.props.weekAmount ? "disabled" : ""
            } ${!this.state.isActive ? "hidden" : ""}`}
            onClick={() => this.props.changeCurrentWeek(1)}
            disabled={this.props.currentWeek >= this.props.weekAmount}
          >
            <span>›</span>
          </button>
        </div>

        <div className="matchup-list" ref={this.listRef}>
          <div
            className={`matchup-carousel ${this.state.isOverflowing ? "" : "no-scroll"}`}
            ref={this.carouselRef}
          >
            {renderMatchups()}
            {this.state.isOverflowing && renderMatchups("dup-")}
          </div>
        </div>
      </div>
    );
  }
}
